import {TOOL_NAMES} from '../constants/tool-names'

const BUILT_IN_RUNTIME_CODE_SEARCH_TOOL_NAMES = new Set<string>([
  TOOL_NAMES.LEXICAL_CODE_SEARCH,
  TOOL_NAMES.SEMANTIC_CODE_SEARCH,
])

const DISPLAY_SUMMARY_PREFIX = 'Display summary:'
const DISPLAY_METADATA_PREFIX = 'Display metadata:'
const REPOSITORY_PREFIX = 'Repository:'

type CodeSearchArguments = {
  query?: unknown
  repo?: unknown
  repository?: unknown
}

export type CodeSearchDisplay = {
  query: string | null
  repository: string | null
  resultCount: string | null
  content: string | undefined
  warnings: string[]
  advisory: string | null
}

export function isCodeSearchToolName(name: string): boolean {
  const normalizedName = name.trim().toLowerCase()
  return (
    BUILT_IN_RUNTIME_CODE_SEARCH_TOOL_NAMES.has(normalizedName) ||
    normalizedName === TOOL_NAMES.GITHUB_MCP_SERVER_SEARCH_CODE
  )
}

export function isGitHubCodeSearchToolCall({name, isMcpTool}: {name: string; isMcpTool?: boolean}): boolean {
  const normalizedName = name.trim().toLowerCase()
  if (normalizedName === TOOL_NAMES.GITHUB_MCP_SERVER_SEARCH_CODE) return true

  return BUILT_IN_RUNTIME_CODE_SEARCH_TOOL_NAMES.has(normalizedName) && !isMcpTool
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized ? normalized : null
}

function repositoryFromLine(line: string | undefined): string | null {
  if (!line?.startsWith(REPOSITORY_PREFIX)) return null
  return normalizeString(line.slice(REPOSITORY_PREFIX.length))
}

function resultCountFromSummary(summary: string): string | null {
  if (/^No results found$/i.test(summary)) return '0'

  const match = summary.match(/^(\d+)(\+?)\s+(?:results?|files?) found$/i)
  if (!match?.[1]) return null
  return `${match[1]}${match[2] ?? ''}`
}

function repositoryFromQuery(query: string | null): string | null {
  return query?.match(/(?:^|\s)repo:([^\s]+)/)?.[1] ?? null
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function queryWithoutRepository(query: string | null, repository: string | null): string | null {
  if (!query || !repository) return query
  return normalizeString(
    query.replace(new RegExp(`(?:^|\\s)repo:${escapeRegex(repository)}`), ' ').replace(/\s+/g, ' '),
  )
}

function parseGitHubMcpOutput(content: string | undefined): {
  resultCount: string | null
  content: string | undefined
} | null {
  if (content === undefined) return null

  let result: unknown
  try {
    result = JSON.parse(content)
  } catch {
    return null
  }
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null

  const record = result as Record<string, unknown>
  if (typeof record.total_count !== 'number' || !Array.isArray(record.items)) return null

  const renderedItems = record.items.flatMap(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return []
    const itemRecord = item as Record<string, unknown>
    const path = normalizeString(itemRecord.path)
    const name = normalizeString(itemRecord.name)
    const sha = normalizeString(itemRecord.sha)
    const repository = normalizeString(itemRecord.repository)
    const location = [repository, path ?? name].filter(Boolean).join(':') || sha
    const fragments = Array.isArray(itemRecord.text_matches)
      ? itemRecord.text_matches.flatMap(match => {
          if (!match || typeof match !== 'object' || Array.isArray(match)) return []
          const fragment = normalizeString((match as Record<string, unknown>).fragment)
          return fragment ? fragment.split('\n') : []
        })
      : []
    const metadata = !path && sha && location !== sha ? [`SHA: ${sha}`] : []
    const lines = [location, ...metadata, ...fragments].filter(value => value !== null)

    return lines.length > 0 ? [lines.join('\n')] : []
  })

  return {
    resultCount: `${record.total_count}${record.incomplete_results === true ? '+' : ''}`,
    content: renderedItems.join('\n\n'),
  }
}

function extractLexicalNotices(content: string | undefined): {
  content: string | undefined
  warnings: string[]
  advisory: string | null
} {
  if (content === undefined) return {content, warnings: [], advisory: null}

  const contentBlocks: string[] = []
  const warnings: string[] = []
  let advisory: string | null = null

  for (const block of content.split(/\n{2,}/)) {
    const lines = block.split('\n')
    if (lines[0] === 'Search warnings:' && lines.slice(1).every(line => line.startsWith('- '))) {
      warnings.push(...lines.slice(1).map(line => line.slice(2)))
      continue
    }

    if (block === 'More matches may exist; narrow the query.') {
      advisory = block
      continue
    }

    contentBlocks.push(block)
  }

  return {content: contentBlocks.join('\n\n').trim(), warnings, advisory}
}

function parseOutput(content: string | undefined): {
  repository: string | null
  resultCount: string | null
  content: string | undefined
} {
  if (content === undefined) {
    return {repository: null, resultCount: null, content: undefined}
  }

  const lines = content.replace(/\r\n/g, '\n').split('\n')
  let resultCount: string | null = null

  while (lines[0]?.startsWith(DISPLAY_SUMMARY_PREFIX) || lines[0]?.startsWith(DISPLAY_METADATA_PREFIX)) {
    const line = lines.shift()
    if (line?.startsWith(DISPLAY_SUMMARY_PREFIX)) {
      resultCount = resultCountFromSummary(line.slice(DISPLAY_SUMMARY_PREFIX.length).trim())
    }
  }

  while (lines[0] === '') lines.shift()
  const repository = repositoryFromLine(lines[0])
  if (repository) lines.shift()
  while (lines[0] === '') lines.shift()

  const cleanedContent = lines.join('\n').trim()
  if (
    resultCount === null &&
    /^(?:Indexed commit: [^\n]+\n\n)?No results (?:found|returned)\.$/i.test(cleanedContent)
  ) {
    resultCount = '0'
  }

  return {repository, resultCount, content: cleanedContent}
}

export function getCodeSearchDisplay({
  args,
  content,
  detailedContent,
  toolName,
}: {
  args: unknown
  content: string | undefined
  detailedContent: string | undefined
  toolName: string
}): CodeSearchDisplay {
  const codeSearchArgs = (args ?? {}) as CodeSearchArguments
  const rawQuery = normalizeString(codeSearchArgs.query)
  const parsedDetailedContent = parseOutput(detailedContent)
  const parsedContent = parseOutput(content)
  const parsedGitHubMcpContent =
    toolName === TOOL_NAMES.GITHUB_MCP_SERVER_SEARCH_CODE
      ? (parseGitHubMcpOutput(detailedContent) ?? parseGitHubMcpOutput(content))
      : null
  const repository =
    normalizeString(codeSearchArgs.repo) ??
    normalizeString(codeSearchArgs.repository) ??
    repositoryFromQuery(rawQuery) ??
    parsedDetailedContent.repository ??
    parsedContent.repository

  const displayContent =
    parsedGitHubMcpContent?.content ??
    (detailedContent === undefined ? parsedContent.content : parsedDetailedContent.content)
  const lexicalContent =
    toolName === TOOL_NAMES.LEXICAL_CODE_SEARCH
      ? extractLexicalNotices(displayContent)
      : {content: displayContent, warnings: [], advisory: null}

  return {
    query: queryWithoutRepository(rawQuery, repository),
    repository,
    resultCount: parsedGitHubMcpContent?.resultCount ?? parsedDetailedContent.resultCount ?? parsedContent.resultCount,
    content: lexicalContent.content,
    warnings: lexicalContent.warnings,
    advisory: lexicalContent.advisory,
  }
}

export function formatCodeSearchResultCount(resultCount: string): string {
  return `${resultCount} ${resultCount === '1' ? 'result' : 'results'}`
}
